import { MouseEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { IconType } from 'react-icons'
import { useLocation } from 'react-router-dom'

import { cn } from '../libs/tailwindMerge'
import { Button } from './Button'

interface DropdownProps {
  title?: string
  icon?: IconType
  toggleIcon?: IconType
  children: ReactNode
  containerClassName?: HTMLDivElement['className']
  triggerClassName?: HTMLDivElement['className']
  customTrigger?: JSX.Element
}

export function Dropdown({
  title = '',
  icon: Icon,
  toggleIcon: ToggleIcon,
  children,
  triggerClassName,
  containerClassName,
  customTrigger,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false)

  const dropdownRef = useRef<HTMLDivElement>(null)

  const { pathname } = useLocation()

  function toggleContainer() {
    setIsOpen(open => !open)
  }

  function handleClickOutside(event: MouseEvent<Document>) {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false)
    }
  }

  function renderIcon() {
    if (Icon && !ToggleIcon) {
      return <Icon aria-hidden="true" />
    } else if (Icon && ToggleIcon) {
      if (isOpen) {
        return <Icon aria-hidden="true" />
      } else {
        return <ToggleIcon aria-hidden="true" />
      }
    }
  }

  useEffect(() => {
    const handleScroll = () => {
      setIsOpen(false)
    }

    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside as unknown as EventListener)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside as unknown as EventListener)
    }
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [pathname])

  return (
    <main ref={dropdownRef} className="relative inline-block text-left">
      <div>
        {customTrigger || (
          <Button
            className={cn(
              'flex items-center gap-2 border-slate-400 text-slate-800 hover:bg-slate-300 hover:text-slate-900',
              triggerClassName,
            )}
            variant="outline"
            onClick={toggleContainer}
          >
            {title}
            {renderIcon()}
          </Button>
        )}
      </div>

      <section
        className={cn(
          'absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none',
          containerClassName,
          !isOpen && 'scale-0',
        )}
      >
        {children}
      </section>
    </main>
  )
}
